import React, { useMemo } from 'react';

import {
	Configuration as LiveExpertApplicantConfiguration,
	ApplicantDocumentControllerApi,
	AuthControllerApi,
	VerificationControllerApi,
	ExpertApplicationControllerApi,
	LookUpControllerApi as LookUpControllerApiApplicant,
	DocumentTypeControllerApi,
	ExpertApplicantControllerApi,
	LiveServiceTypeControllerApi,
	EducationLevelControllerApi,
	BranchControllerApi,
	ContractControllerApi as ContractControllerApiApplicant
} from '@dopingtech/live_expert_applicant_bff_api_ts';

import {
    Configuration as LiveExpertConfiguration,
    CalendarControllerApi,
    AppointmentControllerApi,
    LoginControllerApi,
    PaymentInfoControllerApi,
    ExpertControllerApi,
    PasswordResetControllerApi,
    PayoutControllerApi,
    ExpertSettingControllerApi,
    FeedbackControllerApi,
    LookUpControllerApi,
    EducationLevelControllerApi as EducationLevelControllerApiExpert,
    BranchControllerApi as BranchControllerApiExpert,
    ExpertBranchControllerApi,
    MeetingControllerApi,
    InteractionControllerApi,
    StudentExpertChatControllerApi,
    StudentStatisticControllerApi,
    ContractControllerApi,
    MenuItemControllerApi,
    ExpertSupervisorChatControllerApi,
    SupervisorControllerApi,
    TechnicalSupportControllerApi,
    ChatMetricControllerApi,
    SolverQuestionControllerApi,
    MeasurementEvaluationControllerApi,
    ReportedQuestionControllerApi,
    ReportReasonControllerApi,
    AvailableHourControllerApi,
    SlotLimitControllerApi,
    ExpertNotificationControllerApi,
    BackupRoomControllerApi,
    PoolControllerApi,
    StudentControllerApi,
    ExpertParentChatControllerApi,
    MissionControllerApi,
    ProviderFeedbackControllerApi,
    SupportTicketControllerApi,
    StudentExamControllerApi,
    ExpertReviewControllerApi,
    ExpertResolveQuestionControllerApi,
    SupportDocumentControllerApi,
    MeetingSubjectControllerApi
} from '@dopingtech/live_expert_bff_api_ts';

import { ApiContext } from './api.context';
import { ApiContextProps, ApiProviderProps } from './api.model';
import { useAuthStore } from '@/zustand/auth/store';

export const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
	const { data: authData } = useAuthStore();

	const api = useMemo<ApiContextProps>(() => {
		const liveExpertApplicantConfig = new LiveExpertApplicantConfiguration({
			accessToken: authData?.access_token ?? '',
			basePath: process.env.LIVE_EXPERT_APPLICANT_API_URL!.replace(/\/+$/, '')
		});

		const liveExpertConfig = new LiveExpertConfiguration({
			accessToken: authData?.access_token ?? '',
			basePath: process.env.LIVE_EXPERT_API_URL!.replace(/\/+$/, '')
		});

		const _liveExpertApplicantConfig = {
			ApplicantDocumentControllerApi: new ApplicantDocumentControllerApi(liveExpertApplicantConfig),
			AuthControllerApi: new AuthControllerApi(liveExpertApplicantConfig),
			VerificationControllerApi: new VerificationControllerApi(liveExpertApplicantConfig),
			ExpertApplicationControllerApi: new ExpertApplicationControllerApi(liveExpertApplicantConfig),
			LookUpControllerApiApplicant: new LookUpControllerApiApplicant(liveExpertApplicantConfig),
			DocumentTypeControllerApi: new DocumentTypeControllerApi(liveExpertApplicantConfig),
			ExpertApplicantControllerApi: new ExpertApplicantControllerApi(liveExpertApplicantConfig),
			LiveServiceTypeControllerApi: new LiveServiceTypeControllerApi(liveExpertApplicantConfig),
			EducationLevelControllerApi: new EducationLevelControllerApi(liveExpertApplicantConfig),
			BranchControllerApi: new BranchControllerApi(liveExpertApplicantConfig),
			ContractControllerApiApplicant: new ContractControllerApiApplicant(liveExpertApplicantConfig)
		};

		const _liveExpertConfig = {
			CalendarControllerApi: new CalendarControllerApi(liveExpertConfig),
			AppointmentControllerApi: new AppointmentControllerApi(liveExpertConfig),
			LoginControllerApi: new LoginControllerApi(liveExpertConfig),
			PaymentInfoControllerApi: new PaymentInfoControllerApi(liveExpertConfig),
			ExpertControllerApi: new ExpertControllerApi(liveExpertConfig),
			PasswordResetControllerApi: new PasswordResetControllerApi(liveExpertConfig),
			PayoutControllerApi: new PayoutControllerApi(liveExpertConfig),
			ExpertSettingControllerApi: new ExpertSettingControllerApi(liveExpertConfig),
			FeedbackControllerApi: new FeedbackControllerApi(liveExpertConfig),
			LookUpControllerApi: new LookUpControllerApi(liveExpertConfig),
			EducationLevelControllerApiExpert: new EducationLevelControllerApiExpert(liveExpertConfig),
			BranchControllerApiExpert: new BranchControllerApiExpert(liveExpertConfig),
			ExpertBranchControllerApi: new ExpertBranchControllerApi(liveExpertConfig),
			MeetingControllerApi: new MeetingControllerApi(liveExpertConfig),
			InteractionControllerApi: new InteractionControllerApi(liveExpertConfig),
			StudentStatisticControllerApi: new StudentStatisticControllerApi(liveExpertConfig),
			StudentExpertChatControllerApi: new StudentExpertChatControllerApi(liveExpertConfig),
			ContractControllerApi: new ContractControllerApi(liveExpertConfig),
			MenuItemControllerApi: new MenuItemControllerApi(liveExpertConfig),
			ExpertSupervisorChatControllerApi: new ExpertSupervisorChatControllerApi(liveExpertConfig),
			SupervisorControllerApi: new SupervisorControllerApi(liveExpertConfig),
			ChatMetricControllerApi: new ChatMetricControllerApi(liveExpertConfig),
			TechnicalSupportControllerApi: new TechnicalSupportControllerApi(liveExpertConfig),
			SolverQuestionControllerApi: new SolverQuestionControllerApi(liveExpertConfig),
			MeasurementEvaluationControllerApi: new MeasurementEvaluationControllerApi(liveExpertConfig),
			ReportedQuestionControllerApi: new ReportedQuestionControllerApi(liveExpertConfig),
			ReportReasonControllerApi: new ReportReasonControllerApi(liveExpertConfig),
			AvailableHourControllerApi: new AvailableHourControllerApi(liveExpertConfig),
			SlotLimitControllerApi: new SlotLimitControllerApi(liveExpertConfig),
			ExpertNotificationControllerApi: new ExpertNotificationControllerApi(liveExpertConfig),
			BackupRoomControllerApi: new BackupRoomControllerApi(liveExpertConfig),
			PoolControllerApi: new PoolControllerApi(liveExpertConfig),
			StudentControllerApi: new StudentControllerApi(liveExpertConfig),
			ExpertParentChatControllerApi: new ExpertParentChatControllerApi(liveExpertConfig),
			MissionControllerApi: new MissionControllerApi(liveExpertConfig),
			ProviderFeedbackControllerApi: new ProviderFeedbackControllerApi(liveExpertConfig),
			SupportTicketControllerApi: new SupportTicketControllerApi(liveExpertConfig),
            StudentExamControllerApi: new StudentExamControllerApi(liveExpertConfig),
			ExpertReviewControllerApi: new ExpertReviewControllerApi(liveExpertConfig),
			ExpertResolveQuestionControllerApi: new ExpertResolveQuestionControllerApi(liveExpertConfig),
			SupportDocumentControllerApi: new SupportDocumentControllerApi(liveExpertConfig),
			MeetingSubjectControllerApi: new MeetingSubjectControllerApi(liveExpertConfig)
		};

		return {
			..._liveExpertApplicantConfig,
			..._liveExpertConfig
		};
	}, [authData?.access_token]);

	return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
};
